import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, ArrowLeft, Calendar, User, Clock, Send, RotateCcw, Save, AlertTriangle, FileText, FileDown, Lock, ArrowUp } from 'lucide-react';
import { FeedbackRequest, ManagerFormData, FormErrors, FeedbackAttachment } from '../types/feedback';
import { useApiClient } from '../hooks/useApiClient';
import { useUserProfileContext } from '../context/UserProfileContext';
import { formatDateInIST, formatDateTimeInIST, getEmployeeSubmittedDate, getManagerCompletedDate, mapApiFeedbackRequestFull } from '../utils/mappers';
import { getWordCount } from '../utils/feedbackUtils';
import { useToast } from '../hooks/useToast';
import { Toast, ConfirmDialog } from '../components/Toast';
import { CorePrioritiesView } from '../components/CorePrioritiesView';
import AttachmentList from '../components/AttachmentList';
import { RichTextEditor } from '../components/common/RichTextEditor';
import { isRichTextEmpty, renderRichText } from '../utils/richText';
import { generateConnectPDFContent, printHtmlInNewWindow } from '../utils/pdfTemplates';

export function ProvideManagerReview() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { apiCall } = useApiClient();
  const { userProfile } = useUserProfileContext();
  const { toasts, hideToast, success, error: showError } = useToast();
  
  const [request, setRequest] = useState<FeedbackRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isRequestingRevision, setIsRequestingRevision] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitConfirmMessage, setSubmitConfirmMessage] = useState('');
  const [revisionReason, setRevisionReason] = useState('');
  const [overallComments, setOverallComments] = useState('');
  const [pipEnabled, setPipEnabled] = useState(false);
  const [pipStartDate, setPipStartDate] = useState('');
  const [pipEndDate, setPipEndDate] = useState('');
  const [pipReason, setPipReason] = useState('');
  const [formData, setFormData] = useState<ManagerFormData>({
    feedbacks: []
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [attachments, setAttachments] = useState<FeedbackAttachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const isManagerReviewer = !!request &&
    !!userProfile?.employeeId &&
    (request.managerEmployeeId.toLowerCase() === userProfile.employeeId.toLowerCase() ||
     userProfile.directReports?.some(r => r.employeeId.toLowerCase() === request.employeeId.toLowerCase()));

  // Determine view mode based on status
  const isCompleted = request?.status === 'completed';
  const isRevisionRequested = request?.status === 'revision_requested';
  const isInProgress = request?.status === 'in_progress' || request?.status === 'draft';
  // Editable only when in_progress (or draft); locked for revision_requested and completed
  const isEditable = isInProgress && isManagerReviewer;
  // Show action buttons only when in_progress
  const showActionButtons = isInProgress && isManagerReviewer;

  useEffect(() => {
    if (id) {
      fetchRequestDetails();
      fetchPipStatus();
      fetchAttachments();
    }
  }, [id]);

  // Scroll-to-top
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = useCallback(
    () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    []
  );

  useEffect(() => {
    if (request && request.answers.length > 0) {
      setFormData(prev => ({
        ...prev,
        feedbacks: request.answers.map(a => ({
          questionId: a.questionId,
          managerFeedback: a.managerFeedback || ''
        }))
      }));
      setOverallComments(request.overallComments || '');
    }
  }, [request]);

  const fetchRequestDetails = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${id}`
      );
      if (response.ok) {
        const result = await response.json();
        if (result.data) {
          const mapped = mapApiFeedbackRequestFull(result.data);
          setRequest(mapped);
          // Initialize PIP from the request data
          if (mapped.pip && mapped.pip.isOnPip) {
            setPipEnabled(true);
            setPipStartDate(mapped.pip.pipStartDate ? mapped.pip.pipStartDate.split('T')[0] : '');
            setPipEndDate(mapped.pip.pipEndDate ? mapped.pip.pipEndDate.split('T')[0] : '');
            setPipReason(mapped.pip.pipReason || '');
          }
        }
      } else {
        showError('Failed to fetch request details');
        navigate('/dashboard?tab=team-feedback');
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      showError('Error loading feedback request');
      navigate('/dashboard?tab=team-feedback');
    } finally {
      setLoading(false);
    }
  };

  const fetchPipStatus = async () => {
    if (!id) return;
    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/Pip/feedback-request/${id}`
      );
      if (response.ok) {
        const result = await response.json();
        if (result.data && result.data.isOnPip) {
          setPipEnabled(true);
          setPipStartDate(result.data.pipStartDate ? result.data.pipStartDate.split('T')[0] : '');
          setPipEndDate(result.data.pipEndDate ? result.data.pipEndDate.split('T')[0] : '');
          setPipReason(result.data.pipReason || '');
        }
      }
    } catch (error) {
      console.error('Error fetching PIP status:', error);
    }
  };

  const fetchAttachments = async () => {
    if (!id) return;
    setLoadingAttachments(true);
    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/Attachments/feedback-request/${id}`
      );
      if (response.ok) {
        const result = await response.json();
        if (result.data) {
          setAttachments(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const handleFeedbackChange = (questionId: number, value: string) => {
    const newFeedbacks = [...formData.feedbacks];
    const feedbackIndex = newFeedbacks.findIndex(f => f.questionId === questionId);
    if (feedbackIndex >= 0) {
      newFeedbacks[feedbackIndex] = { ...newFeedbacks[feedbackIndex], managerFeedback: value };
    } else {
      newFeedbacks.push({ questionId, managerFeedback: value });
    }
    setFormData(prev => ({ ...prev, feedbacks: newFeedbacks }));

    if (errors[`feedback_${questionId}`]) {
      setErrors(prev => ({ ...prev, [`feedback_${questionId}`]: undefined }));
    }
  };

  const validateAllFeedbacks = (): boolean => {
    const newErrors: FormErrors = {};
    
    formData.feedbacks.forEach((feedback, index) => {
      const feedbackText = feedback.managerFeedback || '';
      const wordCount = getWordCount(feedbackText);
      const errorKey = feedback.questionId ? `feedback_${feedback.questionId}` : `feedback_${index}`;

      if (wordCount > 1000) {
        newErrors[errorKey] = `Feedback exceeds 1000 words (current: ${wordCount})`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFeedbackSubmissionStats = () => {
    if (!request) {
      return { answeredCount: 0, unansweredCount: 0, totalCount: 0 };
    }

    const totalCount = request.answers.length;
    let answeredCount = 0;

    request.answers.forEach(answer => {
      const feedbackText = formData.feedbacks.find(f => f.questionId === answer.questionId)?.managerFeedback || '';
      if (!isRichTextEmpty(feedbackText)) {
        answeredCount += 1;
      }
    });

    return {
      answeredCount,
      unansweredCount: totalCount - answeredCount,
      totalCount
    };
  };

  const handleSubmit = async () => {
    if (!request || !id) {
      return;
    }

    if (!validateAllFeedbacks()) {
      showError('Please resolve feedback validation errors before submitting.');
      return;
    }

    const { answeredCount, unansweredCount } = getFeedbackSubmissionStats();
    if (answeredCount === 0) {
      showError('Please provide at least one manager feedback before submitting.');
      return;
    }

    // Validate PIP dates if PIP is enabled
    if (pipEnabled && (!pipStartDate || !pipEndDate)) {
      showError('Please provide both start and end dates for the PIP period');
      return;
    }

    if (pipEnabled && isRichTextEmpty(pipReason)) {
      showError('Please provide the reason for PIP');
      return;
    }
    
    if (unansweredCount > 0) {
      setSubmitConfirmMessage(
        `${unansweredCount} question${unansweredCount > 1 ? 's are' : ' is'} unanswered. Do you still want to submit feedback?`
      );
    } else {
      setSubmitConfirmMessage('Are you sure you want to submit all feedback? This action cannot be undone and the employee will be notified.');
    }

    setShowSubmitConfirm(true);
  };

  const savePipStatus = async () => {
    if (!id || !request) return;

    if (!isManagerReviewer || !userProfile?.employeeId) {
      throw new Error('Only the assigned manager can update PIP status for this connect');
    }
    
    try {
      const pipDto = {
        isOnPip: pipEnabled,
        pipStartDate: pipEnabled ? pipStartDate : null,
        pipEndDate: pipEnabled ? pipEndDate : null,
        pipReason: pipEnabled ? pipReason : null,
        userId: userProfile.employeeId,
        userName: userProfile.displayName || request.managerName
      };

      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/Pip/feedback-request/${id}`,
        {
          method: 'POST',
          body: JSON.stringify(pipDto)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save PIP status');
      }
    } catch (error) {
      console.error('Error saving PIP status:', error);
      throw error;
    }
  };

  const confirmSubmit = async () => {
    setShowSubmitConfirm(false);
    if (!request || !id) return;

    if (!isManagerReviewer || !userProfile?.employeeId) {
      showError('Only the assigned manager can submit this review');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Save PIP status first
      await savePipStatus();

      const submitDto = {
        userId: userProfile.employeeId,
        userName: userProfile?.displayName || request.managerName,
        userEmail: userProfile?.email || '',
        overallComments: overallComments,
        feedbacks: formData.feedbacks
      };

      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${id}/manager-feedback`,
        {
          method: 'POST',
          body: JSON.stringify(submitDto)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to submit manager feedback');
      }

      success('Manager feedback submitted successfully!');
      setTimeout(() => navigate('/dashboard?tab=team-feedback'), 1500);
    } catch (error) {
      console.error('Error submitting manager feedback:', error);
      showError('Failed to submit manager feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestRevision = async () => {
    if (!request || !id) return;

    if (!isManagerReviewer || !userProfile?.employeeId) {
      showError('Only the assigned manager can request revision');
      return;
    }

    setIsRequestingRevision(true);
    try {
      const requestRevisionDto = {
        userId: userProfile.employeeId,
        userName: userProfile?.displayName || request.managerName,
        userEmail: userProfile?.email || '',
        reason: revisionReason || 'Please review and revise your self-assessment'
      };

      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${id}/request-revision`,
        {
          method: 'POST',
          body: JSON.stringify(requestRevisionDto)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to request revision');
      }

      success('Revision request sent successfully!');
      setTimeout(() => navigate('/dashboard?tab=team-feedback'), 1500);
    } catch (error) {
      console.error('Error requesting revision:', error);
      showError('Failed to request revision');
    } finally {
      setIsRequestingRevision(false);
      setShowRevisionModal(false);
      setRevisionReason('');
    }
  };

  const handleSaveAsDraft = async () => {
    if (!request || !id) return;

    if (!isManagerReviewer || !userProfile?.employeeId) {
      showError('Only the assigned manager can save this review');
      return;
    }

    if (pipEnabled && (!pipStartDate || !pipEndDate)) {
      showError('Please provide both start and end dates for the PIP period');
      return;
    }

    if (pipEnabled && isRichTextEmpty(pipReason)) {
      showError('Please provide the reason for PIP');
      return;
    }

    setIsSavingDraft(true);
    try {
      // Save PIP status
      await savePipStatus();

      const submitDto = {
        userId: userProfile.employeeId,
        userName: userProfile?.displayName || request.managerName,
        userEmail: userProfile?.email || '',
        overallComments: overallComments,
        feedbacks: formData.feedbacks
      };

      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${id}/manager-feedback-draft`,
        {
          method: 'POST',
          body: JSON.stringify(submitDto)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save draft');
      }

      success('Manager feedback saved as draft!');
      // Refresh the request to get updated data
      await fetchRequestDetails();
    } catch (error) {
      console.error('Error saving draft:', error);
      showError('Failed to save draft');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleExportPDF = async () => {
    if (!request) return;
    setIsExporting(true);
    try {
      let corePriorities = null;
      try {
        const prioritiesResponse = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/api/CorePriorities/employee/${encodeURIComponent(request.employeeId)}/year/${request.year}`
        );
        if (prioritiesResponse.ok) {
          const prioritiesResult = await prioritiesResponse.json();
          if (prioritiesResult.data) {
            corePriorities = prioritiesResult.data;
          }
        }
      } catch (err) {
        console.log('No core priorities found for this connect');
      }

      const pdfContent = generateConnectPDFContent(request, corePriorities);
      printHtmlInNewWindow(pdfContent);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      showError('Failed to generate PDF');
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-pink-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 text-lg">Loading connect request...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center text-slate-900">
          <p className="text-xl mb-4">Connect request not found</p>
          <button
            onClick={() => navigate('/dashboard?tab=team-feedback')}
            className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 px-6 py-2 rounded-lg text-white shadow-lg shadow-pink-500/25"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const employeeSubmittedDate = getEmployeeSubmittedDate(request.timeline) || request.requestDate;
  const managerCompletedDate = getManagerCompletedDate(request.timeline) || (request.status === 'completed' ? request.updatedAt : undefined);

  return (
    <>
      <div className="pt-24 min-h-screen bg-slate-50">
        <div className="mx-auto px-10 lg:px-20 xl:px-32 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard?tab=team-feedback')}
            className="flex items-center gap-2 text-pink-500 hover:text-pink-600 transition-colors mb-4 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>

          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl p-8 text-white shadow-xl">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 text-white rounded-full text-xs font-bold mb-2 backdrop-blur-sm">
                    {isCompleted ? 'View Only' : isRevisionRequested ? 'Pending Revision' : 'Manager Review Mode'}
                  </div>
                  <button
                    onClick={handleExportPDF}
                    disabled={isExporting}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium backdrop-blur-sm transition-colors disabled:opacity-50"
                  >
                    {isExporting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileDown className="w-4 h-4" />
                    )}
                    Export PDF
                  </button>
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  Provide Feedback for {request.employeeName}
                </h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/70">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                    {formatDateTimeInIST(request.requestDate)} IST
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Employee: {request.employeeName}
                </div>
                {request.year && request.quarter && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {request.quarter} {request.year}
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Status Banners */}
        {isCompleted && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <Lock className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-green-700 font-medium text-sm">This connect has been completed and is in view-only mode. No changes can be made.</p>
          </div>
        )}
        {isRevisionRequested && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <p className="text-amber-700 font-medium text-sm">Revision has been requested. Your feedback fields are locked until the employee resubmits their responses.</p>
          </div>
        )}

        {/* Performance Improvement Plan (PIP) Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Performance Improvement Plan (PIP)
            </h3>
            <button
              type="button"
              onClick={() => {
                if (!isEditable) return;
                setPipEnabled(!pipEnabled);
              }}
              disabled={!isEditable}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                pipEnabled ? 'bg-red-500' : 'bg-slate-600'
              } ${!isEditable ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300 ${
                  pipEnabled ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {!pipEnabled ? (
            <p className="text-slate-500 text-sm">
              Toggle on if this employee needs a Performance Improvement Plan. The employee will be notified upon submission.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border-2 border-red-300 rounded-xl">
                <p className="text-red-700 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  This employee will be placed on PIP. They will be able to see this status after you submit your review.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-2">
                    PIP Start Date *
                  </label>
                  <input
                    type="date"
                    value={pipStartDate}
                    onChange={(e) => setPipStartDate(e.target.value)}
                    disabled={!isEditable}
                    className="w-full px-4 py-3 bg-white text-slate-900 rounded-xl border border-slate-300 focus:outline-none focus:border-red-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-2">
                    PIP End Date *
                  </label>
                  <input
                    type="date"
                    value={pipEndDate}
                    onChange={(e) => setPipEndDate(e.target.value)}
                    min={pipStartDate}
                    disabled={!isEditable}
                    className="w-full px-4 py-3 bg-white text-slate-900 rounded-xl border border-slate-300 focus:outline-none focus:border-red-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">
                  Reason for PIP *
                </label>
                <RichTextEditor
                  value={pipReason}
                  onChange={setPipReason}
                  disabled={!isEditable}
                  placeholder="Provide the reason for placing the employee on PIP (required)..."
                  minHeightClassName="min-h-[100px]"
                />
              </div>
            </div>
          )}
        </div>

        {/* Core Priorities - View Only */}
        <CorePrioritiesView
          employeeId={request.employeeId}
          employeeName={request.employeeName}
          year={request.year || new Date().getFullYear()}
        />

        {/* All Questions */}
        <div className="space-y-6">
          {request.answers.map((answer, index) => {
            const question = request.questions?.find(q => q.id === answer.questionId);
            const feedback = formData.feedbacks.find(f => f.questionId === answer.questionId)?.managerFeedback || '';
            const wordCount = getWordCount(feedback);

            return (
              <div
                key={answer.questionId}
                className="rounded-2xl overflow-hidden shadow-sm bg-white border border-slate-200/80 transition-all"
              >
                {/* Question header */}
                <div className="px-5 py-3.5 flex items-center gap-3 border-b bg-gradient-to-r from-slate-50 to-white border-slate-100">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                    {index + 1}
                  </span>
                  <h2 className="text-sm font-semibold text-slate-800 leading-relaxed">
                    {question?.questionText || question?.question}
                  </h2>
                </div>

                {/* Response area */}
                <div className="p-5 space-y-4">
                  {/* Employee's Self-Assessment */}
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <label className="text-[11px] font-semibold text-emerald-500 uppercase tracking-wider">
                        Employee's Response
                      </label>
                      {employeeSubmittedDate && (
                        <span className="text-[11px] text-emerald-600 whitespace-nowrap">
                          {formatDateInIST(employeeSubmittedDate)}
                        </span>
                      )}
                    </div>
                    <div className="bg-emerald-50/50 rounded-xl px-4 py-3 text-slate-700 min-h-[80px] border border-emerald-100">
                      <div
                        className="rich-text-editor"
                        dangerouslySetInnerHTML={renderRichText(answer.answer || 'No response provided')}
                      />
                    </div>
                  </div>

                  {/* Manager's Feedback */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-[11px] font-semibold text-blue-500 uppercase tracking-wider">
                        Manager's Feedback {isEditable && <span className="text-red-400">*</span>}
                      </label>
                      {managerCompletedDate && (
                        <span className="text-[11px] text-blue-400">
                          {formatDateInIST(managerCompletedDate)}
                        </span>
                      )}
                      {isEditable && (
                        <span className={`text-xs font-medium ${
                          wordCount > 1000 ? 'text-red-500' : 'text-slate-400'
                        }`}>
                          {wordCount}/1000 words
                        </span>
                      )}
                    </div>
                    {isEditable ? (
                      <div className="rounded-xl overflow-hidden border border-blue-100 bg-blue-50/30">
                        <RichTextEditor
                          value={feedback}
                          onChange={(updatedValue) => handleFeedbackChange(answer.questionId, updatedValue)}
                          placeholder="Provide your feedback on the employee's response..."
                          minHeightClassName="min-h-[200px]"
                        />
                      </div>
                    ) : (
                      <div className="bg-blue-50/50 rounded-xl px-4 py-3 text-slate-700 min-h-[80px] border border-blue-100">
                        {!isRichTextEmpty(feedback) ? (
                          <div
                            className="rich-text-editor"
                            dangerouslySetInnerHTML={renderRichText(feedback)}
                          />
                        ) : (
                          <p className="text-slate-400 italic">No feedback provided yet</p>
                        )}
                      </div>
                    )}
                    {errors[`feedback_${answer.questionId}`] && (
                      <p className="text-red-500 text-xs mt-1.5">{errors[`feedback_${answer.questionId}`]}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Manager's Overall Comments */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 mt-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Manager's Overall Comments</h3>
          <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-2 text-blue-700 font-medium">
                <span className="text-base">💬</span>
                OVERALL FEEDBACK (Optional)
              </label>
              {isEditable && (
                <span className="text-sm font-medium text-slate-500">
                  {getWordCount(overallComments)}/1000 words
                </span>
              )}
            </div>
            {isEditable ? (
              <RichTextEditor
                value={overallComments}
                onChange={setOverallComments}
                placeholder="Provide overall comments about the employee's performance..."
                minHeightClassName="min-h-[150px]"
              />
            ) : (
              <div className="bg-white/50 rounded-xl px-4 py-3 text-slate-700 min-h-[80px]">
                {!isRichTextEmpty(overallComments) ? (
                  <div
                    className="rich-text-editor"
                    dangerouslySetInnerHTML={renderRichText(overallComments)}
                  />
                ) : (
                  <p className="text-slate-400 italic">No overall comments provided</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Attachments Section */}
        <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-2xl p-5 shadow-sm border border-blue-100 mt-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-blue-100 rounded-lg">
              <FileText className="w-4 h-4 text-blue-500" />
            </div>
            <div>
              <h3 className="text-base font-medium text-slate-800">
                Supporting Documents
              </h3>
              <p className="text-slate-400 text-xs">Documents uploaded by the employee to support their connect request</p>
            </div>
          </div>
          {loadingAttachments ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
            </div>
          ) : attachments.length > 0 ? (
            <AttachmentList
              attachments={attachments}
              feedbackRequestId={parseInt(id || '0')}
              onDelete={async () => {}}
              canDelete={false}
            />
          ) : (
            <div className="text-center py-6 text-slate-400">
              No documents uploaded by the employee.
            </div>
          )}
        </div>

        {/* Submit Footer — only when in_progress */}
        {showActionButtons && (
          <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl p-5 shadow-sm border border-slate-200 mt-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <button
                type="button"
                onClick={() => setShowRevisionModal(true)}
                disabled={isSubmitting || isRequestingRevision || isSavingDraft}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl font-bold shadow-lg transition-all disabled:opacity-50"
              >
                <RotateCcw className="w-5 h-5" />
                Request Revision
              </button>

              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleSaveAsDraft}
                  disabled={isSubmitting || isRequestingRevision || isSavingDraft}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl font-bold shadow-lg shadow-pink-500/25 transition-all disabled:opacity-50"
                >
                  {isSavingDraft ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Save as Draft
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting || isRequestingRevision || isSavingDraft}
                  className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl font-bold shadow-lg shadow-pink-500/25 transition-all disabled:opacity-50"
                >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit All Feedback
                  </>
                )}
              </button>
            </div>
            </div>
          </div>
        )}

        {/* Revision Request Modal */}
        {showRevisionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                <RotateCcw className="w-6 h-6 text-amber-500" />
                Request Revision
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Ask the employee to revise their self-assessment. They will be notified and can update their answers.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Reason for Revision (Optional)
                </label>
                <RichTextEditor
                  value={revisionReason}
                  onChange={setRevisionReason}
                  placeholder="Explain what needs to be revised..."
                  minHeightClassName="min-h-[90px]"
                />
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowRevisionModal(false);
                    setRevisionReason('');
                  }}
                  disabled={isRequestingRevision}
                  className="px-6 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl font-bold transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRequestRevision}
                  disabled={isRequestingRevision}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl font-bold shadow-lg transition-all disabled:opacity-50"
                >
                  {isRequestingRevision ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Requesting...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4" />
                      Send Request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Toasts */}
    {toasts.map(toast => (
      <Toast
        key={toast.id}
        message={toast.message}
        type={toast.type}
        onClose={() => hideToast(toast.id)}
      />
    ))}

    {/* Submit Confirmation Dialog */}
    {showSubmitConfirm && (
      <ConfirmDialog
        title="Submit Manager Feedback"
        message={submitConfirmMessage}
        confirmText="Submit Feedback"
        cancelText="Cancel"
        type="info"
        onConfirm={confirmSubmit}
        onCancel={() => setShowSubmitConfirm(false)}
      />
    )}

    {/* Back to Top FAB */}
    <button
      onClick={scrollToTop}
      className={`
        fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full
        bg-gradient-to-r from-pink-500 to-purple-600 text-white
        shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40
        flex items-center justify-center
        transition-all duration-300 hover:scale-110
        ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}
      `}
      aria-label="Back to top"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
    </>
  );
}
